class FmProductImages extends HTMLElement {
  connectedCallback() {
    this._onSectionSelect = this._handleSectionSelect.bind(this);
    document.addEventListener('shopify:section:select', this._onSectionSelect);

    this._isCarouselMode = this.dataset.mode === 'carousel';
    this._carouselBindings = [];

    if (!this._isCarouselMode) {
      return;
    }

    this.querySelectorAll('.fm-product-images__carousel-wrapper').forEach((wrapper) => {
      const carousel = wrapper.querySelector('flickity-carousel');

      if (!carousel) {
        return;
      }

      const binding = {
        carousel,
        prevButton: wrapper.querySelector('[data-action="prev"]'),
        nextButton: wrapper.querySelector('[data-action="next"]'),
        onCarouselClick: (event) => this._handleCarouselClick(event, carousel),
        onCarouselMouseMove: (event) => this._handleCarouselMouseMove(event, carousel),
        onCarouselMouseLeave: () => this._handleCarouselMouseLeave(carousel),
        onFlickityReady: () => this._handleFlickityReady(binding),
        onSlideChanged: () => this._handleSlideChanged(binding),
      };

      carousel.addEventListener('click', binding.onCarouselClick);
      carousel.addEventListener('mousemove', binding.onCarouselMouseMove);
      carousel.addEventListener('mouseleave', binding.onCarouselMouseLeave);
      carousel.addEventListener('flickity:ready', binding.onFlickityReady);
      carousel.addEventListener('flickity:slide-changed', binding.onSlideChanged);

      if (carousel.classList.contains('flickity-enabled')) {
        binding.onFlickityReady();
      }

      this._carouselBindings.push(binding);
    });
  }

  disconnectedCallback() {
    if (this._onSectionSelect) {
      document.removeEventListener('shopify:section:select', this._onSectionSelect);
    }

    this._carouselBindings.forEach((binding) => {
      binding.carousel.removeEventListener('click', binding.onCarouselClick);
      binding.carousel.removeEventListener('mousemove', binding.onCarouselMouseMove);
      binding.carousel.removeEventListener('mouseleave', binding.onCarouselMouseLeave);
      binding.carousel.removeEventListener('flickity:ready', binding.onFlickityReady);
      binding.carousel.removeEventListener('flickity:slide-changed', binding.onSlideChanged);
    });

    this._carouselBindings = [];
  }

  _handleSectionSelect(event) {
    const sectionElement = event.target;

    if (!sectionElement || !sectionElement.contains(this)) {
      return;
    }

    this.querySelectorAll('flickity-carousel').forEach((carousel) => {
      carousel.dispatchEvent(new CustomEvent('flickity:section-selected', { bubbles: true }));
    });
  }

  _handleCarouselClick(event, carousel) {
    if (window.matchMedia('(max-width: 740px)').matches || !carousel) {
      return;
    }

    const rect = carousel.getBoundingClientRect();
    const center = rect.left + rect.width / 2;

    if (event.clientX > center) {
      carousel.next();
    } else {
      carousel.previous();
    }
  }

  _handleCarouselMouseMove(event, carousel) {
    if (window.matchMedia('(max-width: 740px)').matches || !carousel) {
      return;
    }

    const rect = carousel.getBoundingClientRect();
    const center = rect.left + rect.width / 2;
    const isHoverRight = event.clientX > center;

    carousel.classList.toggle('is-hovering-right', isHoverRight);
    carousel.classList.toggle('is-hovering-left', !isHoverRight);
  }

  _handleCarouselMouseLeave(carousel) {
    if (!carousel) {
      return;
    }

    carousel.classList.remove('is-hovering-right', 'is-hovering-left');
  }

  _handleFlickityReady(binding) {
    if (!binding?.carousel) {
      return;
    }

    binding.carousel.classList.remove('is-pending-ready');
    this._updateNavButtons(binding);
  }

  _handleSlideChanged(binding) {
    this._updateNavButtons(binding);
  }

  async _updateNavButtons(binding) {
    if (!binding?.carousel || !binding.prevButton || !binding.nextButton) {
      return;
    }

    const flickityInstance = await binding.carousel.flickityInstance;

    if (!flickityInstance) {
      return;
    }

    if (flickityInstance.options.wrapAround) {
      binding.prevButton.disabled = false;
      binding.nextButton.disabled = false;
      return;
    }

    const lastIndex = flickityInstance.slides.length - 1;

    binding.prevButton.disabled = flickityInstance.selectedIndex === 0;
    binding.nextButton.disabled = flickityInstance.selectedIndex === lastIndex;
  }
}

if (!customElements.get('fm-product-images')) {
  customElements.define('fm-product-images', FmProductImages);
}
